import { useState, useEffect } from "react";
import { toast } from "sonner";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell, Legend } from 'recharts';
import { api } from "../lib/api";
import { translateRisk } from "../lib/constants";

const ComplianceDashboardPage = () => {
  const [kpiData, setKpiData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Translate severity data for charts
  const translateChartData = (data) => {
    if (!data) return [];
    return data.map(item => ({
      ...item,
      name: translateRisk(item.name) || item.name
    }));
  };

  useEffect(() => {
    const fetchKPIs = async () => {
      try {
        const response = await api.get('/compliance/kpi-dashboard');
        setKpiData(response.data);
      } catch (error) {
        console.error('Error fetching KPIs:', error);
        toast.error('Erro ao carregar KPIs');
      } finally {
        setLoading(false);
      }
    };
    fetchKPIs();
  }, []);

  if (loading) {
    return (<div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div></div>);
  }

  const COLORS = ['#ef4444', '#f59e0b', '#eab308', '#22c55e', '#3b82f6', '#8b5cf6'];

  return (
    <div className="space-y-6 animate-fade-in" data-testid="compliance-dashboard-page">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold font-['Plus_Jakarta_Sans']">Dashboard de Compliance</h1>
        <p className="text-muted-foreground">KPIs e métricas de conformidade</p>
      </div>

      {/* Summary KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="rounded-xl border bg-card p-4">
          <p className="text-sm text-muted-foreground">Conflitos Ativos</p>
          <p className="text-3xl font-bold text-red-500">{kpiData?.summary?.active_conflicts || 0}</p>
          <p className="text-xs text-muted-foreground mt-1">de {kpiData?.summary?.total_conflicts || 0} total</p>
        </div>
        <div className="rounded-xl border bg-card p-4">
          <p className="text-sm text-muted-foreground">Taxa de Resolução</p>
          <p className="text-3xl font-bold text-emerald-500">{kpiData?.summary?.resolution_rate || 0}%</p>
          <p className="text-xs text-muted-foreground mt-1">conflitos resolvidos</p>
        </div>
        <div className="rounded-xl border bg-card p-4">
          <p className="text-sm text-muted-foreground">Conformidade RGPC</p>
          <p className="text-3xl font-bold text-blue-500">{kpiData?.summary?.rgpc_compliance_rate || 0}%</p>
          <p className="text-xs text-muted-foreground mt-1">ofertas conformes</p>
        </div>
        <div className="rounded-xl border bg-card p-4">
          <p className="text-sm text-muted-foreground">Conflitos por NIF</p>
          <p className="text-3xl font-bold text-amber-500">{kpiData?.summary?.nif_conflicts || 0}</p>
          <p className="text-xs text-muted-foreground mt-1">cruzamento de NIFs</p>
        </div>
      </div>

      {/* Charts Row 1 */}
      <div className="grid md:grid-cols-2 gap-6">
        <div className="rounded-xl border bg-card p-6">
          <h2 className="text-lg font-semibold mb-4">Conflitos por Severidade</h2>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={translateChartData(kpiData?.conflicts_by_severity)} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label={({ name, value }) => `${name}: ${value}`}>
                  {(kpiData?.conflicts_by_severity || []).map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.name === 'critical' ? '#ef4444' : entry.name === 'high' ? '#f59e0b' : entry.name === 'medium' ? '#eab308' : '#22c55e'} />
                  ))}
                </Pie>
                <Tooltip /><Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="rounded-xl border bg-card p-6">
          <h2 className="text-lg font-semibold mb-4">Conflitos por Tipo de Entidade</h2>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={kpiData?.conflicts_by_entity_type || []}>
                <CartesianGrid strokeDasharray="3 3" /><XAxis dataKey="name" /><YAxis /><Tooltip />
                <Bar dataKey="value" fill="#3b82f6" radius={[4, 4, 0, 0]}>
                  {(kpiData?.conflicts_by_entity_type || []).map((entry, index) => (<Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Temporal Evolution */}
      <div className="rounded-xl border bg-card p-6">
        <h2 className="text-lg font-semibold mb-4">Evolução Temporal (6 meses)</h2>
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={kpiData?.monthly_evolution || []}>
              <CartesianGrid strokeDasharray="3 3" /><XAxis dataKey="month" /><YAxis /><Tooltip /><Legend />
              <Line type="monotone" dataKey="conflicts" stroke="#ef4444" strokeWidth={2} name="Conflitos Detetados" />
              <Line type="monotone" dataKey="offers" stroke="#3b82f6" strokeWidth={2} name="Ofertas" />
              <Line type="monotone" dataKey="resolved" stroke="#22c55e" strokeWidth={2} name="Resolvidos" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Charts Row 2 */}
      <div className="grid md:grid-cols-2 gap-6">
        <div className="rounded-xl border bg-card p-6">
          <h2 className="text-lg font-semibold mb-4">Conflitos por Tipo</h2>
          <div className="space-y-3">
            {(kpiData?.conflicts_by_type || []).map((item, idx) => (
              <div key={idx} className="flex items-center justify-between">
                <span className="text-sm capitalize">{item.name.replace(/_/g, ' ')}</span>
                <div className="flex items-center gap-2">
                  <div className="w-32 h-2 bg-muted rounded-full overflow-hidden">
                    <div className="h-full bg-primary rounded-full" style={{ width: `${(item.value / (kpiData?.summary?.total_conflicts || 1)) * 100}%` }} />
                  </div>
                  <span className="text-sm font-medium w-8">{item.value}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-xl border bg-card p-6">
          <h2 className="text-lg font-semibold mb-4">Estado das Ofertas</h2>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={[{ name: 'Pendentes', value: kpiData?.offers_stats?.pending || 0 }, { name: 'Aprovadas', value: kpiData?.offers_stats?.approved || 0 }, { name: 'Rejeitadas', value: kpiData?.offers_stats?.rejected || 0 }]} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={40} outerRadius={80} label>
                  <Cell fill="#f59e0b" /><Cell fill="#22c55e" /><Cell fill="#ef4444" />
                </Pie>
                <Tooltip /><Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-4 pt-4 border-t border-border text-center">
            <p className="text-sm text-muted-foreground"><span className="font-medium text-amber-500">{kpiData?.offers_stats?.exceeding_limit || 0}</span> ofertas excedem limites configurados</p>
          </div>
        </div>
      </div>

      {/* Conflict Status Distribution */}
      <div className="rounded-xl border bg-card p-6">
        <h2 className="text-lg font-semibold mb-4">Distribuição por Status</h2>
        <div className="grid grid-cols-5 gap-4">
          {(kpiData?.conflicts_by_status || []).map((item, idx) => (
            <div key={idx} className="text-center p-4 rounded-lg bg-muted/50">
              <p className="text-2xl font-bold">{item.value}</p>
              <p className="text-sm text-muted-foreground capitalize">{item.name}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ComplianceDashboardPage;
